import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import sideimg from "../../assets/images/faqs.jpg";
export default function PriorHfaq() {
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = [
  {
    q: "How does this solution ensure compliance with the CMS-0057-F mandate?",
    a: "Our architecture implements the complete HL7® FHIR® Da Vinci framework (CRD, DTR, and PAS). It enables automated, electronic prior authorization exchanges with straight-through adjudication, meeting the statutory 72-hour expedited and 7-day standard decision deadlines natively.",
  },
  {
    q: "Does this require replacing or heavily modifying our existing EHR systems?",
    a: "No. The solution operates via non-invasive, standard-based CDS Hooks and SMART-on-FHIR apps compatible with Epic, Oracle Health (Cerner), Athenahealth, and MEDITECH. Clinicians trigger authorization rules within their normal charting workflow without leaving the EHR.",
  },
  {
    q: "How are complex clinical guidelines and medical policies evaluated?",
    a: "We leverage Camunda 8’s DMN (Decision Model and Notation) engines combined with Clinical Quality Language (CQL). When an order is placed, payer-specific medical necessity criteria are queried and evaluated against the patient’s FHIR chart automatically, flagging only non-qualifying edge cases for human review.",
  },
  {
    q: "How do you handle health plans that still only accept legacy X12 278 EDI files?",
    a: "Our Zeebe external workers incorporate dual-translation layers. They can ingest modern FHIR Claim/ClaimResponse payloads from providers, map and translate them to standard ASC X12 278 transactions for legacy clearinghouses or payers, and translate the response back into real-time FHIR bundles for the EHR.",
  },
  {
    q: "How is patient ePHI protected during workflow orchestration?",
    a: "Workflows run in a zero-persistence runtime state. Microservice workers evaluate data transiently in-memory with strict garbage collection post-execution. All communications use mTLS 1.3 and scoped OAuth 2.0 tokens, backed by enterprise HIPAA Business Associate Agreements (BAAs) and SOC 2 Type II controls.",
  },

];

  return (
    <section className="w-full relative lg:py-24 md:py-16 py-10 px-6 sm:px-8 lg:px-12">
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
        style={{
          background:
            "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
        }}
      />
      <div className="3xl:w-4/5 xl:w-[90%] mx-auto space-y-16">
        <div className="flex flex-col items-center text-center md:mb-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full backdrop-blur-md shadow-xs mb-3"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600">
              CLEAR ANSWERS
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#000036]"
          >
            Frequently Asked{" "}
            <span className="italic font-serif font-normal text-[#5dc9f0]">
              Questions
            </span>{" "}
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-7 space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div
                  key={idx}
                  className={`border rounded-2xl transition-all duration-200 overflow-hidden ${
                    isOpen
                      ? "bg-white border-slate-300 shadow-sm"
                      : "bg-white/60 border-slate-200/80 hover:border-slate-300"
                  }`}
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                    className="w-full p-6 text-left flex items-center justify-between gap-4 transition-colors"
                  >
                    <span className="text-base sm:text-lg font-bold text-[#000036] tracking-tight">
                      {faq.q}
                    </span>
                    <div
                      className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                        isOpen
                          ? "bg-[#000036] text-white"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {isOpen ? <Minus size={15} /> : <Plus size={15} />}
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                      >
                        <div className="px-6 pb-6 pt-1 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="relative rounded-xl  shadow-sm overflow-hidden space-y-6">
              <div className="relative w-full h-72 sm:h-80 rounded-xl overflow-hidden bg-slate-50 border border-slate-200/80 group">
                <img
                  src={sideimg}
                  alt="Camunda 8 Process Engine Architecture"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
